import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * <product-personalization> — orchestre le flow de personnalisation LFC.
 *
 * États (attribut data-state, piloté aussi en CSS via :has) :
 *  - "empty" : pas de design → CTA "Personnaliser" visible, variantes + panier masqués
 *  - "ready" : design présent (?d= ou sessionStorage) → variantes + panier visibles,
 *              carte "Ta personnalisation", popup taille/gamme à l'arrivée depuis l'éditeur
 */
class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    const dataScript = this.querySelector('script[type="application/json"][data-ref="perso-data"]');
    if (!dataScript) return;

    this.data = JSON.parse(dataScript.textContent);
    this.productForm = this.closest('product-form');
    this.designInput = this.querySelector('[data-ref="design-id-input"]');
    this.summary = this.querySelector('[data-ref="perso-summary"]');
    this.dialogComponent = this.querySelector('dialog-component');
    this.dialogModal = this.querySelector('[data-ref="popup-modal"]');
    this.confirmButton = this.querySelector('[data-ref="perso-confirm"]');

    this.#realignEditorOrigin();
    this.designId = this.#resolveDesignId();

    // Interception de l'ajout au panier : retire d'abord toute ancienne ligne du
    // même design (cas du « Modifier » depuis le panier, sinon doublon/fusion)
    this.addToCartForm = this.productForm?.querySelector('form[action*="/cart/add"]');
    if (this.addToCartForm) {
      this.onAddSubmit = (event) => this.#replaceStaleLineOnSubmit(event);
      document.addEventListener('submit', this.onAddSubmit, { capture: true });

      // Après un ajout réussi, la fiche revient à l'état initial : le design est
      // dans le panier, le client peut lancer une nouvelle personnalisation
      this.onCartUpdated = (event) => this.#resetAfterAdd(event);
      document.addEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    }

    // stopPropagation : le changement d'étape redimensionne le dialog pendant la
    // propagation du clic ; s'il atteint le handler backdrop de popup.js, les
    // coordonnées du clic tombent hors du nouveau rectangle → fermeture parasite
    // L'activation d'un label (clic ou clavier) synthétise un clic sur l'input
    // caché avec des coordonnées (0,0) : le handler backdrop de popup.js le
    // prendrait pour un clic hors du dialog et fermerait la popup.
    this.dialogModal?.querySelector('.dialog-modal__content')?.addEventListener('click', (event) => {
      if (event.clientX === 0 && event.clientY === 0) event.stopPropagation();
    });

    this.confirmButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#confirmChoice();
    });

    // Paire générique par défaut : « Ajouter au panier » sans design → même
    // popup taille/gamme (+ quantité), puis soumission du formulaire natif
    this.querySelector('[data-ref="generic-add"]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#startGenericAdd();
    });
    this.dialogModal?.addEventListener('close', () => {
      // Popup refermée sans valider : on abandonne l'ajout générique en cours
      if (!this.pendingGenericSubmit) this.genericAdd = false;
    });
    this.querySelectorAll('[data-ref="perso-qty-choice"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#confirmQuantite(button.dataset.value);
      });
      const title = button.querySelector('.perso-qty-card__title');
      if (title) this.#colorizeDiscount(title);
    });
    this.querySelectorAll('[data-ref="perso-delete-link"]').forEach((link) =>
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.#resetDesign();
      }),
    );
    this.dialogModal?.addEventListener('change', () => {
      this.#filterTaillesByGamme();
      this.#updatePopupPrices();
    });
    this.#setupGammeDependentTailles();

    // Gamme-dépendante : au changement de gamme sur le picker natif, reset
    // propre AVANT que variant-picker ne traite l'événement (phase capture) :
    // taille à re-choisir, « Une paire » par défaut, prix 1 paire affiché.
    // Sélection incomplète → le picker ne requête aucune variante invalide
    // (c'était la cause du prix « NaN € » et des quantités barrées).
    this.productForm?.addEventListener('change', (event) => this.#onNativeGammeChange(event), { capture: true });

    // Après chaque re-rendu de variante : re-décoration + sauvegarde des choix
    // pour les restaurer au retour de l'éditeur (la page recharge sur la variante
    // par défaut sinon)
    this.productForm?.addEventListener(ThemeEvents.variantUpdated, () => {
      this.#decorateNativeGamme();
      this.#decorateNativeQuantite();
      this.#updateCoffretUpsell();
      this.#persistSelections();
      if (this.pendingGenericSubmit) this.#submitGenericAdd();
    });
    this.#updateCoffretUpsell();

    // Clic « Coffret » (carte popup OU clone du picker natif) : on emporte la
    // paire en cours vers le composer via un seed localStorage. Phase capture :
    // s'exécute avant les gardes stopPropagation de la popup.
    (this.productForm || this).addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-ref="coffret-link"]')) this.#writeCoffretSeed();
      },
      { capture: true },
    );

    // Upsell coffret : toute la carte sélectionne/désélectionne, aucun lien ne
    // navigue vers la fiche produit
    this.productForm
      ?.querySelector('[data-ref="toggle-cross-sells"]')
      ?.addEventListener('click', (event) => {
        const card = event.target.closest('[data-ref="toggle-cross-sell"]');
        if (!card) return;

        const checkbox = card.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
        if (!checkbox) return;

        if (event.target.closest('a')) event.preventDefault();

        // Clic sur la checkbox ou son label : le toggle natif suffit
        if (event.target === checkbox || event.target.closest('label')?.contains(checkbox)) return;

        checkbox.click();
      });

    // Mode standard (produit non personnalisable) : pas de flow design, mais on
    // active les décorations partagées — carte Coffret, prix quantités, gammes
    if (this.dataset.persoEnabled === 'false') {
      this.designId = null;
      this.#decorateNativeGamme();
      this.#decorateNativeQuantite();
      return;
    }

    if (this.designId) {
      this.#enterReadyState();
    } else {
      this.dataset.state = 'empty';
    }
  }

  /**
   * L'origin de retour est rendu en Liquid via request.origin, qui pointe vers le
   * domaine canonique de la boutique — faux en theme dev (127.0.0.1:9292) et en
   * preview. On le réaligne sur l'origine réellement visitée.
   */
  #realignEditorOrigin() {
    const returnUrl = window.location.origin + window.location.pathname;

    const rewrite = (urlString) => {
      const url = new URL(urlString, window.location.origin);
      url.searchParams.set('origin', returnUrl);
      return url.toString();
    };

    try {
      this.data.editorUrl = rewrite(this.data.editorUrl);
      const cta = this.querySelector('[data-ref="customize-link"]');
      if (cta?.href) cta.href = rewrite(cta.href);
    } catch {
      // URL invalide : on garde les liens rendus en Liquid
    }
  }

  disconnectedCallback() {
    if (this.onAddSubmit) {
      document.removeEventListener('submit', this.onAddSubmit, { capture: true });
    }
    if (this.onCartUpdated) {
      document.removeEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    }
  }

  /** Reset de la fiche une fois le design ajouté au panier (déclenché par la soumission de CE formulaire). */
  #resetAfterAdd(event) {
    if (!this.awaitingAdd) return;
    if (event.detail?.data?.source !== 'product-form' || event.detail?.data?.didError) return;

    this.awaitingAdd = false;
    this.#resetDesign();
  }

  /**
   * Un designId = une seule ligne de panier. Au ré-ajout après un « Modifier »
   * depuis le panier, l'ancienne ligne du même design est retirée juste avant
   * l'ajout, puis la soumission originale est rejouée.
   */
  #replaceStaleLineOnSubmit(event) {
    if (event.target !== this.addToCartForm || !this.designId) return;
    if (this.stalePruned) {
      this.stalePruned = false;
      this.awaitingAdd = true;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#removeCartLinesForDesign()
      .catch(() => {
        // Panier illisible : on laisse l'ajout se faire quand même
      })
      .finally(() => {
        this.stalePruned = true;
        this.addToCartForm.requestSubmit(event.submitter || undefined);
      });
  }

  async #removeCartLinesForDesign() {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const stale = (cart.items || []).filter((item) => item.properties?.designId === this.designId);

    let updatedCart = null;
    for (const item of stale) {
      updatedCart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      }).then((response) => response.json());
    }

    // Le badge du header est incrémenté de façon optimiste à l'ajout : sans cette
    // notification, la ligne retirée resterait comptée (ex. 1 retiré + 1 ajouté → « 2 »)
    if (updatedCart && typeof updatedCart.item_count === 'number') {
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: updatedCart.item_count, source: 'product-personalization' }),
      );
    }
  }

  // ——— Résolution du design (URL puis sessionStorage) ———

  #storageKey() {
    return `lfc-perso-${this.data.productId}`;
  }

  #resolveDesignId() {
    const fromUrl = new URLSearchParams(window.location.search).get('d');
    if (fromUrl) return fromUrl;

    try {
      const stored = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
      return stored?.d || null;
    } catch {
      return null;
    }
  }

  #persist(patch) {
    try {
      const current = JSON.parse(sessionStorage.getItem(this.#storageKey()) || '{}');
      sessionStorage.setItem(this.#storageKey(), JSON.stringify({ ...current, ...patch }));
    } catch {
      // stockage indisponible : le flow fonctionne quand même via l'URL
    }
  }

  /** Sauvegarde les valeurs sélectionnées du picker natif (taille, gamme, quantité…). */
  #persistSelections() {
    if (!this.designId) return;

    const selections = {};
    for (const name of this.data.optionNames) {
      const value = this.#selectedNativeValue(name);
      if (value) selections[name] = value;
    }
    this.#persist({ selections });
  }

  /** Réapplique les choix sauvegardés (retour de l'éditeur = rechargement sur la variante par défaut). */
  #restoreSelections() {
    // Variante explicite dans l'URL (retour depuis le panier) : elle prime sur la session
    if (new URLSearchParams(window.location.search).has('variant')) return;

    try {
      const stored = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
      const selections = stored?.selections;
      if (!selections) return;

      for (const [name, value] of Object.entries(selections)) {
        this.#applyToNativePicker(name, value);
      }
    } catch {
      // stockage indisponible : on reste sur la variante par défaut
    }
  }

  #isConfirmed() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
      return stored?.d === this.designId && stored?.confirmed === true;
    } catch {
      return false;
    }
  }

  // ——— État "ready" ———

  #enterReadyState() {
    this.dataset.state = 'ready';
    this.#persist({ d: this.designId });

    // Renseigne la line item property pour l'ajout au panier
    if (this.designInput) {
      this.designInput.name = 'properties[designId]';
      this.designInput.value = this.designId;
    }

    // Liens "Modifier" → rouvrent l'éditeur sur le design existant
    const editUrl = `${this.data.editorUrl}&d=${encodeURIComponent(this.designId)}`;
    this.querySelectorAll('[data-ref="perso-edit-link"]').forEach((link) => (link.href = editUrl));

    // L'éditeur ajoute &thumb= à son URL de retour : sa présence signale un
    // retour frais (perso initiale, modification ou duplication depuis le panier)
    const freshFromEditor = new URLSearchParams(window.location.search).has('thumb');

    // Garde ?d= dans l'URL pour survivre à un rechargement, et purge le thumb consommé
    const url = new URL(window.location.href);
    let urlChanged = false;
    if (url.searchParams.get('d') !== this.designId) {
      url.searchParams.set('d', this.designId);
      urlChanged = true;
    }
    if (url.searchParams.has('thumb')) {
      url.searchParams.delete('thumb');
      urlChanged = true;
    }
    if (urlChanged) history.replaceState({}, '', url.toString());

    this.#mountStickySummary();
    this.#loadThumbnails();
    this.#decorateNativeGamme();
    this.#decorateNativeQuantite();
    this.#restoreSelections();

    // Popup taille + gamme : à chaque retour d'éditeur, ou si jamais validé
    if ((freshFromEditor || !this.#isConfirmed()) && this.dialogModal) {
      this.dialogModal.dataset.step = 'choix';
      this.#syncPopupFromNativePicker();
      this.#updatePopupPrices();
      requestAnimationFrame(() => this.#openPopup());
    }
  }

  /**
   * Préselectionne taille et gamme dans la popup depuis le picker natif quand la
   * page est ouverte sur une variante explicite (?variant=), c.-à-d. au retour
   * d'un Modifier/Dupliquer depuis le panier : on repropose les choix d'origine.
   */
  #syncPopupFromNativePicker() {
    if (!new URLSearchParams(window.location.search).has('variant')) return;

    const pairs = [
      ['perso-taille', this.data.tailleOption],
      ['perso-gamme', this.data.gammeOption],
    ];
    for (const [inputName, optionName] of pairs) {
      if (!optionName) continue;
      const value = this.#selectedNativeValue(optionName);
      if (!value) continue;
      const input = this.dialogModal.querySelector(
        `input[name="${inputName}"][value="${CSS.escape(value)}"]`,
      );
      if (input) input.checked = true;
    }
  }

  #openPopup() {
    const dialog = this.dialogModal;
    if (!dialog) return;

    // Ouverture directe du <dialog> natif : DialogComponent (popup.js) reste
    // en charge de la fermeture (bouton, backdrop, Escape). Idempotent + retry
    // car le timing d'upgrade des custom elements varie selon les navigateurs.
    const tryOpen = () => {
      if (dialog.open) return true;
      try {
        dialog.showModal();
        dialog.scrollTop = 0;
        document.body.classList.add('overflow-hidden');
        return true;
      } catch {
        return false;
      }
    };

    if (!tryOpen()) setTimeout(tryOpen, 300);
  }

  // ——— Thumbnails ———

  /**
   * Déplace le rappel compact (thumbnails + Modifier) dans le contenu de la barre
   * sticky d'ajout au panier : quand elle se fixe en bas, la personnalisation
   * reste visible et modifiable juste au-dessus du bouton.
   */
  #mountStickySummary() {
    const summary = this.querySelector('[data-ref="perso-sticky-summary"]') || this.stickySummary;
    const stickyContent = this.productForm?.querySelector('sticky-add-to-cart .sticky-add-to-cart__content');
    if (!summary || !stickyContent) return;

    const editLink = summary.querySelector('[data-ref="perso-sticky-edit"]');
    if (editLink) editLink.href = `${this.data.editorUrl}&d=${encodeURIComponent(this.designId)}`;

    summary.hidden = false;
    stickyContent.prepend(summary);
    this.stickySummary = summary;
  }

  #loadThumbnails() {
    const containers = [
      ...this.querySelectorAll('[data-ref="perso-thumbs"]'),
      ...(this.stickySummary ? this.stickySummary.querySelectorAll('[data-ref="perso-sticky-thumbs"]') : []),
    ];
    if (!containers.length) return;

    fetch(`${this.data.editorBase}/design/thumbnails/${encodeURIComponent(this.designId)}`)
      .then((response) => {
        // 400/404 = design inexistant → on repart d'un état propre.
        // Toute autre erreur (5xx, réseau) est transitoire : on garde le design.
        if (response.status === 400 || response.status === 404) {
          this.#resetDesign();
          throw new Error('design introuvable');
        }
        if (!response.ok) throw new Error(`thumbnails ${response.status}`);
        return response.json();
      })
      .then((images) => {
        if (!Array.isArray(images) || images.length === 0) return;

        containers.forEach((container) => {
          container.innerHTML = '';
          // Pas de loading=lazy : le bandeau sticky est display:none tant que la
          // barre n'est pas active, le lazy y différerait le chargement des images
          images.forEach((src) => {
            const img = document.createElement('img');
            img.src = src;
            img.alt = this.data.labels.thumbnailAlt;
            container.appendChild(img);
          });
        });
      })
      .catch((error) => {
        console.error('Personnalisation : thumbnails indisponibles', error);
      });
  }

  #resetDesign() {
    try {
      sessionStorage.removeItem(this.#storageKey());
    } catch {}

    if (this.designInput) {
      this.designInput.removeAttribute('name');
      this.designInput.value = '';
    }

    // Retour à l'URL produit nue : on retire TOUS les paramètres (d, thumb…)
    const url = new URL(window.location.href);
    url.search = '';
    history.replaceState({}, '', url.toString());

    this.designId = null;
    this.dataset.state = 'empty';
    if (this.stickySummary) this.stickySummary.hidden = true;
    this.dialogComponent?.close();
  }

  // ——— Options & prix ———

  #optionIndex(optionName) {
    return this.data.optionNames.findIndex((name) => name === optionName);
  }

  #selectedNativeValue(optionName) {
    const picker = this.productForm?.querySelector('variant-picker');
    const input = picker?.querySelector(`input[name="option-${CSS.escape(optionName)}"]:checked`);
    if (input) return input.value;

    const select = picker?.querySelector(`select[name="option-${CSS.escape(optionName)}"]`);
    return select?.value ?? null;
  }

  /**
   * Trouve la variante correspondant à un jeu de valeurs {optionName: value}.
   * Les options absentes du jeu gardent la valeur sélectionnée du picker natif.
   */
  #findVariant(overrides) {
    const wanted = this.data.optionNames.map(
      (name) => overrides[name] ?? this.#selectedNativeValue(name),
    );

    return this.data.variants.find((variant) =>
      variant.options.every((value, index) => wanted[index] == null || wanted[index] === value),
    );
  }

  /**
   * Prix + delta d'une valeur de gamme par rapport à la première gamme du produit,
   * à taille et quantité constantes.
   */
  #gammePricing(gammeValue, tailleOverride) {
    const { gammeOption, tailleOption } = this.data;
    const overrides = { [gammeOption]: gammeValue };
    if (tailleOverride && tailleOption) overrides[tailleOption] = tailleOverride;

    const variant = this.#findVariant(overrides);
    if (!variant) return null;

    const baseOverrides = { ...overrides, [gammeOption]: this.data.gammeValues[0] };
    const baseVariant = this.#findVariant(baseOverrides);
    const delta = baseVariant ? variant.price - baseVariant.price : 0;

    return { price: variant.price, delta, available: variant.available };
  }

  #pricingLabel(pricing) {
    if (!pricing) return '';
    if (pricing.delta > 0) return `+ ${formatMoney(pricing.delta)}`;
    return this.data.labels.standardPrice;
  }

  // ——— Popup taille + gamme ———

  #popupValue(name) {
    return this.dialogModal?.querySelector(`input[name="${name}"]:checked`)?.value ?? null;
  }

  // ——— Tailles dépendantes de la gamme (ex. Carte Blanche) ———
  // Quand toutes les combinaisons gamme × taille n'existent pas (grilles de
  // tailles différentes par gamme), la gamme se choisit EN PREMIER dans la
  // popup et les tailles proposées suivent la gamme sélectionnée. Détection
  // par les données : les produits à matrice complète ne changent pas.

  #taillesForGamme(gamme) {
    const gammeIndex = this.data.optionNames.indexOf(this.data.gammeOption);
    const tailleIndex = this.data.optionNames.indexOf(this.data.tailleOption);
    const tailles = new Set();
    for (const variant of this.data.variants) {
      if (variant.options[gammeIndex] === gamme) tailles.add(variant.options[tailleIndex]);
    }
    return tailles;
  }

  #setupGammeDependentTailles() {
    const { gammeOption, tailleOption, gammeValues } = this.data;
    if (!this.dialogModal || !gammeOption || !tailleOption || gammeValues.length < 2) return;

    const signatures = gammeValues.map((gamme) => [...this.#taillesForGamme(gamme)].sort().join('|'));
    this.gammeDependentTailles = signatures.some((signature) => signature !== signatures[0]);
    if (!this.gammeDependentTailles) return;

    const tailleFieldset = this.dialogModal.querySelector('input[name="perso-taille"]')?.closest('fieldset');
    const gammeFieldset = this.dialogModal.querySelector('input[name="perso-gamme"]')?.closest('fieldset');
    if (tailleFieldset && gammeFieldset) {
      tailleFieldset.parentNode.insertBefore(gammeFieldset, tailleFieldset);
    }

    // Ici la gamme EST le produit (Multisport/Running/Cyclisme) : la carte
    // affiche la valeur d'option, pas le titre marketing du metaobject
    // (ex. « Confort » pour la gamme running sur les autres fiches), et le
    // picto suit le sport (coureur pour running, pas le nuage « Confort »)
    this.dialogModal.querySelectorAll('input[name="perso-gamme"]').forEach((input) => {
      const card = input.closest('label');
      if (card) this.#overrideGammeCard(card, input.value);
    });

    // Tailles toujours présentées de S à XL, quel que soit l'ordre des
    // valeurs côté Shopify (re-dérivé de l'ordre des variantes)
    const rank = { S: 0, M: 1, L: 2, XL: 3 };
    const container = this.dialogModal.querySelector('input[name="perso-taille"]')?.closest('.variant-picker__option-values');
    if (container) {
      [...container.querySelectorAll('.variant-picker__option-value')]
        .sort((a, b) => {
          const letter = (el) => (el.querySelector('input')?.value ?? '').split(' ')[0];
          return (rank[letter(a)] ?? 99) - (rank[letter(b)] ?? 99);
        })
        .forEach((el) => container.appendChild(el));
    }

    this.#filterTaillesByGamme();
    // Le picker natif aussi, dès l'init : sur un chargement frais sans design
    // (#decorateNativeGamme pas encore passé), la gamme par défaut est cochée
    // mais les tailles des AUTRES gammes restaient visibles, mélangées.
    this.#filterNativeTailles();
  }

  /** Carte gamme en mode gamme-dépendante : titre = valeur d'option, picto du sport. */
  #overrideGammeCard(card, value) {
    if (!this.gammeDependentTailles) return;
    const ICON_OVERRIDES = { running: 'directions_run' };

    const title = card.querySelector('.perso-gamme-card__title');
    if (title && !title.querySelector('img')) title.textContent = value;

    const overrideIcon = ICON_OVERRIDES[value.toLowerCase()];
    const iconElement = card.querySelector('theme-icon');
    const previousIcon = iconElement?.dataset.iconName;
    if (overrideIcon && iconElement && previousIcon && previousIcon !== overrideIcon) {
      iconElement.classList.remove(`icon--${previousIcon}`);
      iconElement.classList.add(`icon--${overrideIcon}`);
      iconElement.dataset.iconName = overrideIcon;
      const url = iconElement.style.getPropertyValue('--icon-url');
      if (url) iconElement.style.setProperty('--icon-url', url.replaceAll(`/${previousIcon}/`, `/${overrideIcon}/`));
    }
  }

  /**
   * Picker natif en mode gamme-dépendante : ne montre que les tailles de la
   * gamme sélectionnée, triées S→XL, avec la disponibilité recalculée pour
   * cette gamme (le rendu Liquid la fige sur la variante courante).
   */
  #filterNativeTailles() {
    if (!this.gammeDependentTailles) return;
    const { gammeOption, tailleOption } = this.data;
    const picker = this.productForm?.querySelector('variant-picker');
    if (!picker || !tailleOption || !gammeOption) return;
    const gamme = this.#selectedNativeValue(gammeOption);
    if (!gamme) return;

    const inputs = [...picker.querySelectorAll(`input[name="option-${CSS.escape(tailleOption)}"]`)];
    if (!inputs.length) return;

    const allowed = this.#taillesForGamme(gamme);
    const gammeIndex = this.data.optionNames.indexOf(gammeOption);
    const tailleIndex = this.data.optionNames.indexOf(tailleOption);
    const availability = new Map();
    for (const variant of this.data.variants) {
      if (variant.options[gammeIndex] !== gamme) continue;
      const taille = variant.options[tailleIndex];
      availability.set(taille, Boolean(availability.get(taille)) || Boolean(variant.available));
    }

    const wrapperOf = (input) => input.closest('.variant-picker__option-value') || input;
    const rank = { S: 0, M: 1, L: 2, XL: 3 };
    const container = wrapperOf(inputs[0])?.parentElement;
    if (container) {
      [...inputs]
        .sort((a, b) => (rank[a.value.split(' ')[0]] ?? 99) - (rank[b.value.split(' ')[0]] ?? 99))
        .forEach((input) => container.appendChild(wrapperOf(input)));
    }
    inputs.forEach((input) => {
      const wrapper = wrapperOf(input);
      const visible = allowed.has(input.value);
      wrapper.hidden = !visible;
      if (visible) {
        if (availability.get(input.value)) input.removeAttribute('aria-disabled');
        else input.setAttribute('aria-disabled', 'true');
      }
    });
  }

  /** Reset du picker natif au changement de gamme (mode gamme-dépendante). */
  #onNativeGammeChange(event) {
    if (!this.gammeDependentTailles) return;
    const { gammeOption, tailleOption, quantiteOption } = this.data;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== `option-${gammeOption}`) return;
    const picker = target.closest('variant-picker');
    if (!picker) return;

    // Le mode « choix explicite » du thème gère la sélection incomplète et le
    // bouton « Choisissez votre taille » ; on l'active si la page a chargé
    // avec ?variant= (auquel cas il est absent du rendu)
    picker.setAttribute('data-require-choice', '');

    // Taille à re-choisir pour la nouvelle gamme
    if (tailleOption) {
      picker.querySelectorAll(`input[name="option-${CSS.escape(tailleOption)}"]`).forEach((input) => {
        input.checked = false;
      });
    }

    // Quantité par défaut : « Une paire » (première valeur), toutes recliquables
    if (quantiteOption) {
      [...picker.querySelectorAll(`input[name="option-${CSS.escape(quantiteOption)}"]`)].forEach((input, index) => {
        input.removeAttribute('aria-disabled');
        input.checked = index === 0;
      });
    }

    this.#filterNativeTailles();
    this.#decorateNativeQuantite();
    this.#displayGammeUnitPrice(target.value);
  }

  /** Affiche le prix « une paire » de la gamme dans le prix principal de la fiche. */
  #displayGammeUnitPrice(gamme) {
    const { gammeOption, quantiteOption, optionNames } = this.data;
    const gammeIndex = optionNames.indexOf(gammeOption);
    const quantiteIndex = quantiteOption ? optionNames.indexOf(quantiteOption) : -1;
    const firstQuantite = quantiteIndex >= 0
      ? this.productForm?.querySelector(`variant-picker input[name="option-${CSS.escape(quantiteOption)}"]`)?.value
      : null;
    const variant = this.data.variants.find(
      (v) => v.options[gammeIndex] === gamme && (quantiteIndex < 0 || v.options[quantiteIndex] === firstQuantite),
    );
    if (!variant) return;

    const section = this.closest('.shopify-section') || document;
    const priceElement = section.querySelector('[data-ref="price"]');
    if (priceElement) {
      priceElement.textContent = formatMoney(variant.price);
      priceElement.dataset.value = String(variant.price);
    }
  }

  #filterTaillesByGamme() {
    if (!this.gammeDependentTailles || !this.dialogModal) return;

    const gamme = this.#popupValue('perso-gamme');
    const tailleFieldset = this.dialogModal.querySelector('input[name="perso-taille"]')?.closest('fieldset');
    if (!tailleFieldset) return;

    // Tant qu'aucune gamme n'est choisie, les tailles attendent leur tour
    tailleFieldset.hidden = !gamme;
    if (!gamme) return;

    const allowed = this.#taillesForGamme(gamme);

    // Disponibilité recalculée pour LA gamme choisie : le aria-disabled rendu
    // par Liquid est figé sur la gamme par défaut, ce qui barrait à tort les
    // tailles propres aux autres gammes
    const gammeIndex = this.data.optionNames.indexOf(this.data.gammeOption);
    const tailleIndex = this.data.optionNames.indexOf(this.data.tailleOption);
    const availability = new Map();
    for (const variant of this.data.variants) {
      if (variant.options[gammeIndex] !== gamme) continue;
      const taille = variant.options[tailleIndex];
      availability.set(taille, Boolean(availability.get(taille)) || Boolean(variant.available));
    }

    this.dialogModal.querySelectorAll('input[name="perso-taille"]').forEach((input) => {
      const wrapper = input.closest('.variant-picker__option-value') || input;
      const visible = allowed.has(input.value);
      wrapper.hidden = !visible;
      if (visible) {
        if (availability.get(input.value)) input.removeAttribute('aria-disabled');
        else input.setAttribute('aria-disabled', 'true');
      }
      if (!visible && input.checked) input.checked = false;
    });
  }

  #updatePopupPrices() {
    if (!this.dialogModal) return;

    const taille = this.#popupValue('perso-taille');

    this.dialogModal.querySelectorAll('input[name="perso-gamme"]').forEach((input) => {
      const priceElement = this.dialogModal.querySelector(
        `[data-ref="perso-gamme-price"][data-value="${CSS.escape(input.value)}"]`,
      );
      if (!priceElement) return;

      const pricing = this.#gammePricing(input.value, taille);
      if (this.gammeDependentTailles) {
        // Gammes = produits distincts (Carte Blanche) : prix TOTAL de chaque
        // gamme, pas un delta ni « Tarif standard »
        priceElement.textContent = pricing ? formatMoney(pricing.price) : '';
        priceElement.classList.remove('perso-gamme-card__price--delta');
      } else {
        priceElement.textContent = this.#pricingLabel(pricing);
        priceElement.classList.toggle('perso-gamme-card__price--delta', Boolean(pricing && pricing.delta > 0));
      }
    });

    // Valider n'est actif qu'une fois taille ET gamme choisies (quand elles existent)
    if (this.confirmButton) {
      const needsTaille = Boolean(this.data.tailleOption && this.data.tailleValues.length);
      const needsGamme = Boolean(this.data.gammeOption && this.data.gammeValues.length);
      const gamme = this.#popupValue('perso-gamme');
      this.confirmButton.disabled = (needsTaille && !taille) || (needsGamme && !gamme);
    }
  }

  // ——— Ajout générique (sans personnalisation) ———

  #startGenericAdd() {
    if (this.designId) return; // design en cours : flux habituel
    this.genericAdd = true;
    if (!this.dialogModal) {
      this.#queueGenericSubmit();
      return;
    }
    this.dialogModal.dataset.step = 'choix';
    this.#syncPopupFromNativePicker();
    this.#updatePopupPrices();
    this.#openPopup();
  }

  /**
   * Les choix viennent d'être appliqués au picker natif : le thème re-rend le
   * formulaire (variantUpdated) avant que l'id de variante soit à jour. On
   * soumet à cet événement, ou après un court délai si rien ne change
   * (variante déjà sélectionnée).
   */
  #queueGenericSubmit() {
    this.genericAdd = false;
    this.pendingGenericSubmit = true;
    clearTimeout(this.genericSubmitTimer);
    this.genericSubmitTimer = setTimeout(() => this.#submitGenericAdd(), 900);
  }

  #submitGenericAdd() {
    if (!this.pendingGenericSubmit) return;
    this.pendingGenericSubmit = false;
    clearTimeout(this.genericSubmitTimer);
    if (this.designInput) {
      this.designInput.removeAttribute('name');
      this.designInput.value = '';
    }
    const form = this.productForm?.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput && idInput.value === '') {
      // Variante non résolue (taille manquante) : on rouvre la popup
      this.genericAdd = true;
      this.#openPopup();
      return;
    }
    form.requestSubmit();
  }

  #confirmChoice() {
    const { tailleOption, gammeOption } = this.data;

    const taille = this.#popupValue('perso-taille');
    const gamme = this.#popupValue('perso-gamme');

    // Les choix ne sont PAS appliqués tout de suite au picker natif : un .click()
    // déclenche un re-rendu de section par le thème qui peut écraser l'état de la
    // popup (retour étape 1, voire fermeture) pendant l'étape quantité. On les
    // garde en attente et on applique tout d'un coup à la fin.
    this.pendingChoice = {};
    if (tailleOption && taille) this.pendingChoice[tailleOption] = taille;
    if (gammeOption && gamme) this.pendingChoice[gammeOption] = gamme;

    // Étape 2 : choix de la quantité (upsell) si le produit a une option quantité
    if (this.data.quantiteOption && this.dialogModal?.querySelector('[data-step-panel="quantite"]')) {
      this.#updateQuantitePrices();
      this.dialogModal.dataset.step = 'quantite';
      this.dialogModal.scrollTop = 0;
    } else {
      this.#finishChoice();
    }
  }

  #confirmQuantite(value) {
    if (this.data.quantiteOption && value) {
      this.pendingChoice = { ...this.pendingChoice, [this.data.quantiteOption]: value };
    }
    this.#finishChoice();
  }

  /** Fin de popup : choix appliqués au picker natif, puis ajout direct (générique) ou mémorisation (design). */
  #finishChoice() {
    const generic = this.genericAdd;
    if (generic) this.#queueGenericSubmit();
    this.#applyPendingChoice();
    if (!generic) this.#persist({ d: this.designId, confirmed: true });
    this.dialogComponent?.close();
  }

  /** Applique les choix en attente au picker natif (déclenche le flow standard du thème). */
  #applyPendingChoice() {
    for (const [optionName, value] of Object.entries(this.pendingChoice || {})) {
      this.#applyToNativePicker(optionName, value);
    }
    this.pendingChoice = null;
  }

  /** Prix de chaque carte quantité, aux taille et gamme choisies à l'étape 1. */
  #updateQuantitePrices() {
    const { quantiteOption } = this.data;

    this.dialogModal.querySelectorAll('[data-ref="perso-qty-price"]').forEach((element) => {
      const variant = this.#findVariant({ ...this.pendingChoice, [quantiteOption]: element.dataset.value });
      element.textContent = variant ? formatMoney(variant.price) : '';
      element.closest('[data-ref="perso-qty-choice"]')?.toggleAttribute(
        'disabled',
        Boolean(variant && variant.available === false),
      );
    });
  }

  #applyToNativePicker(optionName, value) {
    const picker = this.productForm?.querySelector('variant-picker');
    if (!picker) return;

    const input = picker.querySelector(
      `input[name="option-${CSS.escape(optionName)}"][value="${CSS.escape(value)}"]`,
    );
    if (input && !input.checked) {
      input.click();
      return;
    }

    const select = picker.querySelector(`select[name="option-${CSS.escape(optionName)}"]`);
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Affiche sous chaque choix de quantité du picker natif le prix de la variante
   * correspondante, recalculé à chaque changement de gamme ou de taille.
   */
  #decorateNativeQuantite() {
    const { quantiteOption } = this.data;
    if (!quantiteOption) return;

    const picker = this.productForm?.querySelector('variant-picker');
    if (!picker) return;

    const inputs = picker.querySelectorAll(`input[name="option-${CSS.escape(quantiteOption)}"]`);
    inputs[0]?.closest('fieldset')?.classList.add('perso-qty-fieldset');

    inputs.forEach((input) => {
      const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;

      // Titre regroupé dans un span : le label est en colonne, sans regroupement
      // le texte et la réduction colorée deviendraient des lignes séparées
      let titleElement = label.querySelector('.perso-qty-native-title');
      if (!titleElement) {
        titleElement = document.createElement('span');
        titleElement.className = 'perso-qty-native-title';
        titleElement.textContent = label.textContent.trim();
        label.textContent = '';
        label.appendChild(titleElement);
      }
      this.#colorizeDiscount(titleElement);

      let priceElement = label.querySelector('.perso-qty-native-price');
      if (!priceElement) {
        priceElement = document.createElement('span');
        priceElement.className = 'perso-qty-native-price';
        label.appendChild(priceElement);
      }

      let variant = this.#findVariant({ [quantiteOption]: input.value });
      // Gamme-dépendante : taille pas encore choisie après un changement de
      // gamme → le prix ne dépend pas de la taille, on prend la première
      // variante de la gamme pour cette quantité
      if (!variant && this.gammeDependentTailles && this.data.gammeOption) {
        const gamme = this.#selectedNativeValue(this.data.gammeOption);
        const quantiteIndex = this.data.optionNames.indexOf(quantiteOption);
        const gammeIndex = this.data.optionNames.indexOf(this.data.gammeOption);
        variant = this.data.variants.find(
          (v) => v.options[quantiteIndex] === input.value && v.options[gammeIndex] === gamme,
        );
      }
      priceElement.textContent = variant ? formatMoney(variant.price) : '';
    });

    // Carte « Coffret » à la suite des quantités (clonée depuis le gabarit,
    // présent aussi en mode standard) — réinjectée après chaque re-rendu du picker
    const grid = inputs[0]?.closest('.variant-picker__option-values');
    if (grid && !grid.querySelector('.perso-qty-card--coffret')) {
      const coffretCard = this.querySelector('[data-ref="perso-coffret-template"]')?.content.firstElementChild;
      if (coffretCard) grid.appendChild(coffretCard.cloneNode(true));
    }
  }

  /**
   * Upsell coffret adapté à la quantité de chaussettes sélectionnée :
   * une paire (SKU 1-CH-…) → coffret une paire, trois paires (3-CH-…) →
   * pack 3 paires. Jamais sur les produits PACK/coffrets eux-mêmes.
   */
  static UPSELLS_BY_SKU = [
    { pattern: /^1-CH-/, handle: 'coffret-une-paires' },
    { pattern: /^3-CH-/, handle: 'pack-3-paires' },
  ];

  #updateCoffretUpsell() {
    const upsell = this.productForm?.querySelector('[data-ref="toggle-cross-sells"]');
    if (!upsell) return;

    const variant = this.#findVariant({});
    const match = variant?.sku
      ? ProductPersonalization.UPSELLS_BY_SKU.find((u) => u.pattern.test(variant.sku))
      : null;

    upsell.classList.toggle('perso-upsell-visible', Boolean(match));

    // Une seule carte visible : celle du pack correspondant à la quantité.
    // La carte masquée est décochée pour ne pas partir au panier par erreur.
    upsell.querySelectorAll('[data-ref="toggle-cross-sell"]').forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      const visible = Boolean(match && link?.getAttribute('href').includes(match.handle));
      card.style.display = visible ? '' : 'none';

      if (!visible) {
        const checkbox = card.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
        if (checkbox?.checked) checkbox.click();
      }
    });
  }

  /** Emporte la paire en cours (produit + choix + design éventuel) vers le composer de coffret. */
  #writeCoffretSeed() {
    // Les choix faits dans la popup (pendingChoice / radios) priment sur le
    // picker natif, qui n'est synchronisé qu'à la toute fin du parcours popup
    const chosen = (optionName, popupName) =>
      this.pendingChoice?.[optionName] ??
      this.#popupValue(popupName) ??
      (optionName ? this.#selectedNativeValue(optionName) : null);

    try {
      localStorage.setItem(
        'lfc-coffret-seed',
        JSON.stringify({
          productId: this.data.productId,
          taille: this.data.tailleOption ? chosen(this.data.tailleOption, 'perso-taille') : null,
          gamme: this.data.gammeOption ? chosen(this.data.gammeOption, 'perso-gamme') : null,
          designId: this.designId || null,
          thumb: this.querySelector('[data-ref="perso-thumbs"] img')?.src || null,
        }),
      );
    } catch {
      // stockage indisponible : le composer s'ouvrira simplement vierge
    }
  }

  /** Met la réduction en couleur dans un libellé : « Trois paires (-15%) ». */
  #colorizeDiscount(element) {
    if (element.querySelector('.perso-qty-discount')) return;
    element.innerHTML = element.innerHTML.replace(
      /\((-\s*\d+\s*%)\)/,
      '<span class="perso-qty-discount">($1)</span>',
    );
  }

  // ——— Cartes gamme dans le picker natif ———

  #decorateNativeGamme() {
    const { gammeOption } = this.data;
    if (!gammeOption) return;

    const picker = this.productForm?.querySelector('variant-picker');
    if (!picker) return;

    const inputs = picker.querySelectorAll(`input[name="option-${CSS.escape(gammeOption)}"]`);
    if (!inputs.length) return;

    inputs[0].closest('fieldset')?.classList.add('perso-gamme-fieldset');

    // Gammes coupées (rupture) : masque leur carte du picker natif ; si la
    // variante masquée était sélectionnée, bascule sur la première visible.
    // S'il ne reste qu'une gamme visible, il n'y a plus de choix : le
    // fieldset entier est caché (la valeur reste sélectionnée dans le DOM).
    const hiddenGammes = (this.data.hiddenGammes || []).filter(Boolean);
    const isHidden = (value) => hiddenGammes.includes(String(value).trim().toLowerCase());
    let fallbackInput = null;
    let visibleCount = 0;
    let hiddenWasChecked = false;
    inputs.forEach((input) => {
      const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (isHidden(input.value)) {
        if (input.checked) hiddenWasChecked = true;
        input.disabled = true;
        if (label) label.style.display = 'none';
        return;
      }
      visibleCount += 1;
      if (!fallbackInput) fallbackInput = input;
    });
    if (hiddenWasChecked && fallbackInput) {
      fallbackInput.checked = true;
      fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (visibleCount < 2) {
      const fieldset = inputs[0].closest('fieldset');
      if (fieldset) fieldset.hidden = true;
    }

    inputs.forEach((input) => {
      if (isHidden(input.value)) return;
      const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label || label.dataset.persoDecorated) return;

      const meta = this.data.gammes[input.value] || { title: input.value, description: '', iconHtml: '' };
      const pricing = this.#gammePricing(input.value, null);

      // Mode gamme-dépendante (Carte Blanche) : prix TOTAL de la gamme
      const priceHtml = this.gammeDependentTailles
        ? `<span class="perso-gamme-card__price">${pricing ? formatMoney(pricing.price) : ''}</span>`
        : `<span class="perso-gamme-card__price${pricing && pricing.delta > 0 ? ' perso-gamme-card__price--delta' : ''}">${this.#pricingLabel(pricing)}</span>`;

      label.dataset.persoDecorated = 'true';
      label.classList.add('perso-gamme-card');
      label.innerHTML = `
        <span class="perso-gamme-card__icon">${meta.iconHtml || ''}</span>
        <span class="perso-gamme-card__body">
          <span class="perso-gamme-card__title">${meta.titleHtml || meta.title}</span>
          ${meta.description ? `<span class="perso-gamme-card__description">${meta.description}</span>` : ''}
          ${priceHtml}
        </span>
      `;
      this.#overrideGammeCard(label, input.value);
    });

    this.#filterNativeTailles();
  }
}

/**
 * <sticky-personalize> — même comportement que le sticky add-to-cart du thème,
 * appliqué au CTA « Personnaliser » (état vide) : une fois le formulaire produit
 * hors du viewport, le bouton se fixe en bas de l'écran, pleine largeur.
 * Réutilise les classes CSS .sticky-add-to-cart pour un rendu identique.
 */
class StickyPersonalize extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.content = this.querySelector('.sticky-add-to-cart__content');
    this.formInView = true;
    this.footerInView = false;

    if (!this.form || !this.content) return;

    // Réserve la hauteur dans le flux (même garde anti-clignotement que le thème)
    requestAnimationFrame(() => {
      this.style.minHeight = `${this.offsetHeight}px`;
      this.#initObservers();
    });
  }

  disconnectedCallback() {
    this.formObserver?.disconnect();
    this.footerObserver?.disconnect();
  }

  #initObservers() {
    // Même correction que le sticky add-to-cart du thème : on observe le
    // placeholder du CTA (this), pas le formulaire entier, pour que le bouton
    // « Personnaliser » se fixe dès qu'il sort de l'écran.
    this.formObserver = new IntersectionObserver(([entry]) => {
      this.formInView = entry.isIntersecting;
      this.#updateDisplay();
    });
    this.formObserver.observe(this);

    if (!this.footer) return;

    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });
    this.footerObserver.observe(this.footer);
  }

  #updateDisplay() {
    const shouldBeActive = !this.formInView && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      return;
    }

    this.dataset.active = 'false';
    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}

if (!customElements.get('sticky-personalize')) {
  customElements.define('sticky-personalize', StickyPersonalize);
}
